import React from "react";
import { ShieldCheck, Award, Users, Target } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Button } from "./ui/button";
import { media } from "../mock";

const values = [
  {
    icon: <ShieldCheck className="h-6 w-6" />,
    title: "Safety First",
    description:
      "Comprehensive protocols and experienced crews on every site, every day.",
  },
  {
    icon: <Award className="h-6 w-6" />,
    title: "Veteran Excellence",
    description:
      "Military precision and discipline applied to every demolition project.",
  },
  {
    icon: <Users className="h-6 w-6" />,
    title: "Expert Team",
    description:
      "Trained professionals with years of demolition and construction experience.",
  },
  {
    icon: <Target className="h-6 w-6" />,
    title: "Precision Work",
    description:
      "Accurate, efficient demolition that meets timelines and exceeds expectations.",
  },
];

export const About = () => {
  const navigate = useNavigate();

  return (
    <section id="about" data-testid="about-section" className="tex-plate py-20 md:py-28">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Left: image */}
          <div className="relative">
            <div className="absolute -top-4 -left-4 w-24 h-24 border-t-2 border-l-2 border-primary" />
            <img
              src={media.heroBg}
              alt="Robo Demo Co. branded excavator on a demolition site"
              className="w-full h-[480px] object-cover"
              style={{ objectPosition: "center 65%" }}
            />
            <div className="absolute -bottom-4 -right-4 w-24 h-24 border-b-2 border-r-2 border-primary" />
            <div className="absolute bottom-6 left-6 bg-background/90 border border-border px-6 py-4">
              <div className="font-display text-4xl font-black text-primary">
                Est. 2023
              </div>
              <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
                Virginia Based
              </div>
            </div>
          </div>

          {/* Right: content */}
          <div>
            <p className="label-kicker mb-4">About Robo Demo LLC</p>
            <h2 className="font-display text-4xl md:text-5xl font-bold uppercase tracking-tight text-white leading-none mb-6">
              Military Precision,
              <span className="block text-primary">Civilian Results</span>
            </h2>
            <div className="space-y-5 text-gray-400 leading-relaxed">
              <p>
                Robo Demo LLC is a proud service-disabled veteran-owned small
                business based in Virginia, bringing military precision and an
                unwavering commitment to every demolition and site work project.
              </p>
              <p>
                From residential teardowns to large-scale commercial demolition
                and comprehensive site clearing, we handle projects of all sizes
                — primarily across the DMV area with nationwide capabilities.
              </p>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-10">
              {values.map((v) => (
                <div
                  key={v.title}
                  className="card-industrial p-5"
                  data-testid={`value-card-${v.title.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  <div className="w-11 h-11 flex items-center justify-center border border-primary/30 bg-primary/10 text-primary mb-3">
                    {v.icon}
                  </div>
                  <h3 className="font-display text-lg font-bold uppercase tracking-tight text-white">
                    {v.title}
                  </h3>
                  <p className="text-xs text-gray-500 mt-1 leading-relaxed">
                    {v.description}
                  </p>
                </div>
              ))}
            </div>

            <Button
              data-testid="about-learn-more-btn"
              onClick={() => navigate("/about")}
              className="mt-10 rounded-none font-bold uppercase tracking-widest px-8 py-6"
            >
              Our Full Story
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};
